import { Component } from '@theme/component';
import { scheduleWireResourceListCarouselArrows } from '@theme/resource-list-carousel-arrows';

/**
 * Tabbed featured collection section — switches product grids/carousels per tab.
 */
class FeaturedCollectionTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#viewAll = this.querySelector('[data-featured-tabs-view-all]');

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#activateTab(tab));
    });

    this.#tabs.forEach((tab) => {
      tab.addEventListener('keydown', this.#onTabKeyDown);
    });

    this.#mediaQuery = window.matchMedia('(width < 750px)');
    this.#onMediaQueryChange = () => this.#wireCarouselArrows();
    this.#mediaQuery.addEventListener('change', this.#onMediaQueryChange);

    this.#wireCarouselArrows();
  }

  disconnectedCallback() {
    this.#mediaQuery?.removeEventListener('change', this.#onMediaQueryChange);
  }

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLAnchorElement | null} */
  #viewAll = null;

  /** @type {MediaQueryList} */
  #mediaQuery;

  /** @type {() => void} */
  #onMediaQueryChange;

  /**
   * @param {KeyboardEvent} event
   */
  #onTabKeyDown = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = this.#tabs.indexOf(tab);
    if (index < 0) return;

    let nextIndex = index;

    if (event.key === 'ArrowRight') {
      nextIndex = (index + 1) % this.#tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.#tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.#activateTab(this.#tabs[nextIndex]);
    this.#tabs[nextIndex].focus();
  };

  /**
   * @param {HTMLButtonElement} tab
   */
  #activateTab(tab) {
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    this.#tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      item.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      const isActive = panel.id === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    if (this.#viewAll instanceof HTMLAnchorElement) {
      const collectionUrl = tab.dataset.collectionUrl;
      if (collectionUrl) {
        this.#viewAll.href = collectionUrl;
        this.#viewAll.hidden = false;
      } else {
        this.#viewAll.hidden = true;
      }
    }

    const activePanel = this.#panels.find((panel) => panel.id === panelId);
    const slideshow = activePanel ? this.#getPanelSlideshow(activePanel) : null;
    if (slideshow && typeof slideshow.select === 'function') {
      requestAnimationFrame(() => {
        slideshow.select(slideshow.current ?? 0, undefined, { animate: false });
        this.#wireCarouselArrows();
      });
    } else {
      this.#wireCarouselArrows();
    }
  }

  #wireCarouselArrows() {
    const slideshow = this.#getActivePanelSlideshow();
    const slideshowId = slideshow?.id ?? '';

    for (const container of this.querySelectorAll('[data-carousel-arrows]')) {
      if (slideshowId) {
        container.dataset.slideshowId = slideshowId;
      } else {
        delete container.dataset.slideshowId;
      }
      delete container.dataset.carouselArrowsWired;
    }

    scheduleWireResourceListCarouselArrows(this);
  }

  #getActivePanelSlideshow() {
    const activePanel = this.querySelector('.featured-collection-tabs__panel.is-active:not([hidden])');
    return activePanel ? this.#getPanelSlideshow(activePanel) : null;
  }

  /**
   * @param {HTMLElement} panel
   */
  #getPanelSlideshow(panel) {
    for (const slideshow of panel.querySelectorAll('.resource-list__carousel > slideshow-component')) {
      const wrapper = slideshow.closest('.resource-list');
      if (!wrapper || this.#isResourceListVisible(wrapper)) {
        return slideshow;
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} wrapper
   */
  #isResourceListVisible(wrapper) {
    if (wrapper.closest('[hidden]')) return false;

    if (wrapper.classList.contains('hidden--mobile') && this.#mediaQuery.matches) {
      return false;
    }

    if (wrapper.classList.contains('hidden--desktop') && !this.#mediaQuery.matches) {
      return false;
    }

    return true;
  }
}

if (!customElements.get('featured-collection-tabs')) {
  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
